import React, { useEffect, useState } from "react";
import { Cloud, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Compact Salesforce org picker. Activating an org switches the "active" org used
// by extraction / governance / dashboard, then calls onChanged so the page reloads.
export function SfOrgSwitcher({ onChanged, className = "" }) {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const [orgs, setOrgs] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [switching, setSwitching] = useState(false);

  const load = () =>
    api.get("/salesforce/orgs").then((r) => {
      setOrgs(r.data.orgs || []);
      setActiveId(r.data.active_id || "");
    }).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  if (orgs.length < 2) return null; // only useful when there are multiple orgs

  const activate = async (id) => {
    if (id === activeId) return;
    setSwitching(true);
    try {
      await api.post(`/salesforce/orgs/${id}/activate`);
      setActiveId(id);
      const picked = orgs.find((o) => o.id === id);
      toast.success(`Switched to ${picked?.label || "org"}`);
      onChanged && onChanged(id);
    } catch {
      toast.error("Couldn't switch org");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="sf-org-switcher">
      <Cloud className="h-4 w-4 text-primary" />
      <span className="text-xs font-medium text-muted-foreground">Salesforce org</span>
      <Select value={activeId} onValueChange={activate} disabled={switching || restricted}>
        <SelectTrigger className="h-9 w-[220px]" data-testid="sf-org-switcher-select">
          <SelectValue placeholder="Select org" />
        </SelectTrigger>
        <SelectContent>
          {orgs.map((o) => (
            <SelectItem key={o.id} value={o.id} data-testid={`sf-org-opt-${o.id}`}>
              <span className="flex items-center gap-1.5">
                {o.auth_error && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
                {o.label} · {o.environment}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
